import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
} from "react";
import { Shipment, ShipmentOverview } from "../../../Models/Shipment";
import { ShipmentsService } from "../../../services/shipment/shipments.ts";
import { AttachmentsService } from "../../../services/registry/attachments.ts";
import { callErrorToast } from "../../../utilities/utilities";
import { ErrorResponse } from "../../../Models/Error";
import { defaultFetchParam, FetchParam } from "../../../Models/FetchParam.ts";

const shipmentsService = new ShipmentsService();
const attachmentsService = new AttachmentsService();

/**
 * Props for the ShipmentOverviewProvider component.
 */
interface ShipmentOverviewProviderProps {
  id: string;
  children: ReactNode;
  callback?: () => void;
}

/**
 * Context value that will be shared with consumers.
 */
interface ShipmentOverviewContextValue {
  data: Partial<ShipmentOverview>;
  error: ErrorResponse<ShipmentOverview> | null;
  loader: boolean;
  callback?: () => void;
}

// Create the context with an initial value of undefined
const ShipmentOverviewContext = createContext<
  ShipmentOverviewContextValue | undefined
>(undefined);

/**
 * Provider component for managing ShipmentOverview-related data and operations.
 * Handles retrieval, creation, and modification of ShipmentOverviews.
 */
const ShipmentOverviewProvider: React.FC<ShipmentOverviewProviderProps> = ({
  id,
  children,
  callback,
}) => {
  const [data, setData] = useState<Partial<ShipmentOverview>>({});
  const [loader, setLoader] = useState<boolean>(true);
  const [error, setError] = useState<ErrorResponse<ShipmentOverview> | null>(
    null
  );

  /**
   * Fetches ShipmentOverview details by ID.
   * @param id - The ShipmentOverview ID.
   */
  const getShipment = (id: string): Promise<Partial<Shipment>> => {
    setLoader(true);
    return shipmentsService
      .get(id)
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        return err;
      });
  };

  /**
   * Retrieves all available attachments.
   * @returns A promise resolving to an array of attachments.
   */
  const getAttachments = (id: string): Promise<any[]> => {
    const params: FetchParam = {
      ...defaultFetchParam,
      sorting: { selector: "name", orderBy: "asc" },
      search: [
        {
          selector: "shipmentId",
          value: id,
          equals: true,
        },
      ],
    };
    return attachmentsService
      .all(params)
      .then((res) => res.data?.content || [])
      .catch((err) => {
        return err;
      });
  };

  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  useEffect(() => {
    if (id) {
      Promise.all([getShipment(id), getAttachments(id)])
        .then(([shipment, attachments]) => {
          setData({ shipment, attachments });
          setLoader(false);
        })
        .catch((err) => {
          if (callback) {
            callback();
          }
          setError(err);
        });
      getShipment(id);
      getAttachments(id);
    }
  }, []);

  return (
    <ShipmentOverviewContext.Provider
      value={{
        data,
        loader,
        error,
        callback,
      }}
    >
      {children}
    </ShipmentOverviewContext.Provider>
  );
};

/**
 * Custom hook to access the ShipmentOverviewContext.
 * Ensures that the context is used only within a ShipmentOverviewProvider.
 */
const useShipmentOverviewContext = (): ShipmentOverviewContextValue => {
  const context = useContext(ShipmentOverviewContext);
  if (!context) {
    throw new Error(
      "useShipmentOverviewContext must be used within a ShipmentOverviewProvider"
    );
  }
  return context;
};

// Export the provider and custom hook for use in other components
export { ShipmentOverviewProvider, useShipmentOverviewContext };
